import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { faqList } from '@/mocks/faq';
import { getRecentArticles, getArticlesByCategory } from '@/mocks/articles';
import ArticleCard from '@/components/feature/ArticleCard';

export default function Home() {
  const featuredArticles = getRecentArticles(6);
  const harmArticles = getArticlesByCategory('huangwang-weihai').slice(0, 2);
  const preventionArticles = getArticlesByCategory('fangfan-cuoshi').slice(0, 2);

  return (
    <>
      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '黄网识别与防范指南',
            url: 'https://fangfanhuangwang.com',
            description: '深入介绍黄网的定义、危害、识别方法及法律风险，帮助用户掌握有效防范措施，保护个人隐私与网络安全。',
            inLanguage: 'zh-CN',
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '网络安全教育团队',
            url: 'https://fangfanhuangwang.com',
            description: '致力于普及网络安全知识，帮助公众识别和防范黄色网站。',
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer support',
              email: 'contact@wangluoanquan.org',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '黄网识别与防范指南 - 首页',
            url: 'https://fangfanhuangwang.com',
            isPartOf: {
              '@type': 'WebSite',
              name: '黄网识别与防范指南',
            },
          }),
        }}
      />

      <main>
        {/* Hero Section */}
        <section className="relative min-h-[580px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20minimalist%20digital%20safety%20abstract%20background%20with%20soft%20green%20and%20warm%20cream%20gradient%2C%20subtle%20hexagonal%20grid%20pattern%2C%20gentle%20light%20rays%2C%20clean%20modern%20atmosphere%2C%20peaceful%20protective%20feeling%2C%20no%20text%20or%20logos&width=1920&height=1080&seq=home-hero-bg&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-top"
              width={1920}
              height={1080}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/40 via-foreground-900/20 to-foreground-900/40"></div>
          </div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/20 mb-6">
              <div className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-500 text-background-50">
                <i className="ri-shield-line text-sm"></i>
              </div>
              <span className="text-sm font-medium text-background-50">网络安全公益教育平台</span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-6 leading-tight">
              识别与防范
              <span className="block text-accent-300">守护网络安全</span>
            </h1>
            <p className="text-base md:text-lg text-background-200/90 max-w-2xl mx-auto mb-8 leading-relaxed">
              深入了解黄色网站的定义、危害与法律风险，掌握实用的识别方法和防范措施，
              保护自己和家人远离网络色情的侵害，共同营造清朗的网络空间。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/fangfan-cuoshi"
                className="px-8 py-3.5 bg-primary-500 text-background-50 rounded-full font-medium text-base hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                学习防范措施
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/category/huangwang-shibie"
                className="px-8 py-3.5 bg-background-50/15 backdrop-blur-sm text-background-50 border border-background-50/30 rounded-full font-medium text-base hover:bg-background-50/25 transition-colors whitespace-nowrap"
              >
                了解识别方法
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Banner */}
        <section className="bg-primary-500">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { number: '6', label: '知识分类', icon: 'ri-folder-line' },
                { number: '14', label: '深度文章', icon: 'ri-article-line' },
                { number: '8', label: '常见问答', icon: 'ri-question-answer-line' },
                { number: '100%', label: '免费公益', icon: 'ri-heart-line' },
              ].map((stat) => (
                <div key={stat.label} className="text-background-50">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-background-50/20 mb-3">
                    <i className={`${stat.icon} text-lg`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold font-heading">{stat.number}</div>
                  <div className="text-sm text-background-50/80 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-16 md:py-20 bg-background-50" id="category-list">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                知识分类导航
              </h2>
              <p className="text-foreground-500 max-w-xl mx-auto">
                从不同维度深入了解黄网相关问题，选择您感兴趣的分类开始学习
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex items-start gap-4 p-5 rounded-xl bg-background-50 border border-background-200 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-300"
                >
                  <div className="w-12 h-12 shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-1 group-hover:text-primary-600 transition-colors">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      {cat.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-4">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  精选文章
                </h2>
                <p className="text-foreground-500">最新发布的深度文章，助您全面了解黄网防范知识</p>
              </div>
              <Link
                to="/category/huangwang-dingyi"
                className="text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* Harm & Prevention */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Harm Section */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className="ri-alert-line text-lg"></i>
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-foreground-900">黄网危害</h2>
                    <p className="text-sm text-foreground-500">深入了解黄网的严重危害</p>
                  </div>
                </div>
                <div className="space-y-4">
                  {harmArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="block p-4 rounded-xl bg-background-50 border border-background-200 hover:border-accent-200 hover:bg-accent-50/30 transition-all duration-300"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-background-100">
                          <img
                            src={article.imageUrl}
                            alt={article.title}
                            className="w-full h-full object-cover"
                            width={56}
                            height={56}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1 line-clamp-1 hover:text-accent-600 transition-colors">
                            {article.title}
                          </h3>
                          <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-foreground-400 whitespace-nowrap">{article.publishDate}</span>
                            <span className="text-xs text-foreground-300">·</span>
                            <span className="text-xs text-foreground-400 whitespace-nowrap">{article.readTime} 分钟阅读</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                <Link
                  to="/category/huangwang-weihai"
                  className="inline-flex items-center gap-1 mt-5 text-sm text-accent-600 font-medium hover:text-accent-700 transition-colors whitespace-nowrap"
                >
                  更多危害分析
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              {/* Prevention Section */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                    <i className="ri-shield-check-line text-lg"></i>
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-foreground-900">防范措施</h2>
                    <p className="text-sm text-foreground-500">学习有效的防范策略</p>
                  </div>
                </div>
                <div className="space-y-4">
                  {preventionArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="block p-4 rounded-xl bg-background-50 border border-background-200 hover:border-secondary-200 hover:bg-secondary-50/30 transition-all duration-300"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-background-100">
                          <img
                            src={article.imageUrl}
                            alt={article.title}
                            className="w-full h-full object-cover"
                            width={56}
                            height={56}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1 line-clamp-1 hover:text-secondary-600 transition-colors">
                            {article.title}
                          </h3>
                          <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-foreground-400 whitespace-nowrap">{article.publishDate}</span>
                            <span className="text-xs text-foreground-300">·</span>
                            <span className="text-xs text-foreground-400 whitespace-nowrap">{article.readTime} 分钟阅读</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                <Link
                  to="/category/fangfan-cuoshi"
                  className="inline-flex items-center gap-1 mt-5 text-sm text-secondary-600 font-medium hover:text-secondary-700 transition-colors whitespace-nowrap"
                >
                  更多防范措施
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                常见问题解答
              </h2>
              <p className="text-foreground-500">关于黄网识别与防范的常见疑问，这里有您需要的答案</p>
            </div>
            <div className="space-y-3">
              {faqList.map((faq, index) => (
                <details key={index} className="group bg-background-50 rounded-xl border border-background-200 overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-50 transition-colors list-none">
                    <span className="font-medium text-foreground-800 pr-4">{faq.question}</span>
                    <span className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 group-open:bg-primary-100 group-open:text-primary-600 transition-colors">
                      <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqList.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.answer,
                },
              })),
            }),
          }}
        />

        {/* CTA Section */}
        <section className="py-16 md:py-20 bg-primary-500">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
              携手共建清朗网络空间
            </h2>
            <p className="text-background-50/90 mb-8 leading-relaxed">
              网络安全需要每个人的参与。学习防范知识，保护自己和家人，举报不良网站，
              让我们一起为净化网络环境贡献力量。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/about"
                className="px-8 py-3.5 bg-background-50 text-primary-600 rounded-full font-medium text-base hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                了解更多
              </Link>
              <a
                href="https://www.12377.cn"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="px-8 py-3.5 bg-primary-600 text-background-50 rounded-full font-medium text-base hover:bg-primary-700 transition-colors whitespace-nowrap"
              >
                举报不良网站
                <i className="ri-external-link-line ml-2"></i>
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}